import classNames from 'classnames';
import React from 'react';

import {Button, Picture} from '@udemy/react-core-components';

import styles from './messaging-banner.module.less';

import type {Tile} from './types';

export interface MessagingBannerVisual {
    backgroundStyle: React.CSSProperties;
    endImageArea?: React.ReactNode;
    size?: 'default' | 'small';
}

export interface MessagingBannerProps {
    tile: Tile;
    visual: MessagingBannerVisual;
}

/**
 * Surface-agnostic "elevated banner" renderer for a single CVR-2667 messaging tile: a
 * card with a colored/gradient background, content on one side and an image on the
 * other. Shared by both Bento's grid and Tabs' full-width single-banner layout — no
 * Bento- or Tabs-specific sizing or assumptions belong in this file.
 *
 * Built from plain elements rather than `@udemy/react-messaging-components`' `Banner` —
 * `Banner`'s image-area grid column is sized to the image's own intrinsic
 * width (`auto`), not a proportion of the card, so it can't produce a clean split; this
 * component's flex layout mirrors `pp-banner.module.less`'s proven pattern instead
 * (image sized to the card's height, width follows its own aspect ratio).
 */
export const MessagingBanner = ({tile, visual}: MessagingBannerProps) => {
    const size = visual.size ?? 'default';
    const imageArea =
        visual.endImageArea ??
        (tile.imageUrl && (
            <Picture
                src={tile.imageUrl}
                sources={[]}
                alt=""
                className={styles['image-fill']}
                imgClassName={styles.image}
            />
        ));

    return (
        <div
            className={styles.banner}
            data-purpose="messaging-banner"
            style={visual.backgroundStyle}
        >
            <div className={classNames(styles.content, styles[`content-${size}`])}>
                {tile.eyebrow && (
                    <span className={classNames('ud-heading-xl', styles.eyebrow)}>
                        {tile.eyebrow}
                    </span>
                )}
                <h3
                    className={classNames(
                        size === 'small' ? 'ud-heading-xl' : 'ud-heading-xxl',
                        styles.headline,
                        styles[`headline-${size}`],
                    )}
                >
                    {tile.headline}
                </h3>
                {tile.description && (
                    <p
                        className={classNames(
                            size === 'small' ? ['ud-heading-sm', 'ud-text-bold'] : 'ud-text-sm',
                            styles.description,
                            styles[`description-${size}`],
                        )}
                    >
                        {tile.description}
                    </p>
                )}
                <Button
                    componentClass="a"
                    href={tile.ctaUrl}
                    udStyle="white-solid"
                    className={styles.cta}
                >
                    {tile.ctaLabel}
                </Button>
                {tile.footnote && (
                    <p
                        className={classNames('ud-text-xs', styles.footnote)}
                        data-purpose="messaging-banner-footnote"
                    >
                        {tile.footnote}
                    </p>
                )}
            </div>
            {imageArea && <div className={styles['image-wrapper']}>{imageArea}</div>}
        </div>
    );
};
